import React, { useEffect, useState, useCallback } from 'react'
import { useAtomValue, useAtom } from 'jotai'
import { MiniAppText } from '@/ui/molecules/mini-app-text'
import { EditableInput } from '@sourcepilot/ui/framework/ui/molecules/EditableInput'
import { DeleteConfirmationDialog } from '@sourcepilot/ui/framework/ui/molecules/DeleteConfirmationDialog'
import { MiniAppProps } from './types'
import { NewTabData, useTabData, chatTransitionAtom } from '@sourcepilot/ui/framework/state/session-data';
import { productMiniAppStateAtom } from '@/state/rflp-session';import { ChatTask } from '@sourcepilot/generated/pdm_tasks/api/chattask'
import { Workspace } from '@sourcepilot/generated/base/api/workspace'
import { useNode } from '@sourcepilot/framework/relationship-manager'
import { useEditable } from '@sourcepilot/ui/framework/data/hooks/use-editable'
import { usePathname } from 'next/navigation'
import { Trash2 } from 'lucide-react'
import { graphStore } from '@sourcepilot/framework/graph-store'
import { modelForLabel, getRouteForModel } from '@sourcepilot/ui/framework/data/route-resolver'
import { QueryNames } from '@sourcepilot/generated/query-names';

// Separate component for task item to use hooks
function TaskItem({
  task,
  containerWidth,
  onDelete
}: {
  task: ChatTask
  containerWidth: number
  onDelete: (taskId: string) => void
}) {
  const [ deleteAnchor, setDeleteAnchor ] = useState<HTMLElement | null>(null)
  const [ deletingId, setDeletingId ] = useState<string | null>(null)
  const { activeTab, navigateToTab } = useTabData()
  const [, setIsTransitioning] = useAtom(chatTransitionAtom)
  const pathname = usePathname()

  // Get the thread from the task for display (name)
  const thread = useNode(task.thread)

  // Create editable wrapper for the thread (used for inline editing)
  const threadEditable = useEditable(thread)

  // Get the attached entity from the task (Task-First architecture)
  const attachedEntity = useNode(task.currently_attached_to)

  // Resolve task label for navigation (taskClassName on the tab).
  const taskLabel = (task as any)._label || 'ChatTask'

  // Map Neo4j label to route content type. PDM action/part keep bespoke
  // segments (they carry productSlug / nested routes handled below); every
  // other attachable artifact (e.g. an RFLP `Project`) resolves generically
  // through the route resolver so the chat redirects to its detail page with
  // the chat docked in the side panel — same behaviour PDMProduct has always had.
  const getContentType = (label: string | undefined): string => {
    if (!label) return 'chat';
    switch (label) {
      case 'PDMProduct': return 'product';
      case 'PDMAction': return 'action';
      case 'PDMPart': return 'part';
    }
    // Backend may stamp a Neo4j label ("RFLPProject") or class name ("Project");
    // modelForLabel normalises both to the route-map model key.
    const model = modelForLabel(label);
    const route = getRouteForModel(model);
    const segment = route?.path?.replace(/^\//, '').split('/')[0];
    return segment || 'chat';
  };

  const isDeleting = deletingId === task.id

  // Display name: always use thread name (the editable field)
  const displayName = (thread?.name as string) || "Untitled Chat"

  const handleTaskClick = () => {
    if (!task.id) {
      console.error('[ChatsMiniApp] Task missing ID:', task);
      return;
    }

    if (attachedEntity) {
      // Task attached to an entity - navigate to entity page with chat sidebar
      if (!attachedEntity.id) {
        console.error('[ChatsMiniApp] Attached entity missing ID:', attachedEntity);
        return;
      }

      const contentType = getContentType((attachedEntity as any)?._label);
      const tab: NewTabData = {
        id: attachedEntity.id as string,
        title: displayName,
        contentType,
        productSlug: contentType === 'action'
          ? (attachedEntity as any).product_id || (attachedEntity as any).productId
          : undefined,
        taskClassName: taskLabel,
      };

      // Determine transition type based on current location
      const isChatPage = pathname.includes('/chat/');

      if (isChatPage) {
        // Coming from standalone chat -> shrink animation (fullscreen to sidebar)
        setIsTransitioning('shrink');
      } else {
        // Coming from product/action/part page -> slide animation (sidebar slides in)
        setIsTransitioning('slide');
      }

      // Use task ID in the URL (Task-First architecture)
      // agent param is added automatically by navigateToTab from tab.taskClassName
      navigateToTab(tab, `?chat=${task.id}`)
    } else {
      // Standalone chat - navigate to chat page (fullscreen)
      const tab: NewTabData = {
        id: task.id as string,
        title: displayName,
        contentType: "chat",
        taskClassName: taskLabel,
      };

      // No transition for standalone chat (it's fullscreen)
      navigateToTab(tab)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteAnchor(e.currentTarget as HTMLElement)
  }

  const confirmDelete = async () => {
    setDeletingId(task.id!)
    try {
      await task.delete({ cascade: true })
      onDelete(task.id!)
      console.log('Task deleted successfully:', task.id)
    } catch (err) {
      console.error('Failed to delete task:', err)
      alert(`Failed to delete task: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setDeleteAnchor(null)
      setDeletingId(null)
    }
  }

  return (
    <li className="leading-none pl-2 py-1.5">
      <div className="flex items-center gap-1 group min-w-0">
        {/* Name - using EditableInput for inline editing.
            The edit + delete icons render in a right-anchored hover overlay
            (via EditableInput's overlayActions) that floats over the text,
            so the title always uses the full mini-app width. */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={handleTaskClick}
        >
          {threadEditable ? (
            <EditableInput
              editable={threadEditable as any}
              field="name"
              placeholder="Untitled Chat"
              className="text-sm"
              showEditIcon={true}
              showSaveIcon={false}
              showCancelIcon={false}
              minWidth="auto"
              hoverHighlight={false}
              truncate={true}
              overlayActions={
                <button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400 p-0.5 flex-shrink-0"
                  title="Delete chat"
                >
                  {isDeleting ? (
                    <span className="text-xs">...</span>
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              }
            />
          ) : (
            <MiniAppText
              className="text-sm"
              containerWidth={containerWidth - 96}
              isActive={activeTab?.id === (attachedEntity ? attachedEntity.id : task.id)}
              isHovered={false}
            >
              {displayName}
            </MiniAppText>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <DeleteConfirmationDialog
        anchor={deleteAnchor}
        itemType="chat"
        itemName={displayName}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteAnchor(null)}
      />
    </li>
  )
}

export function ChatsMiniApp({ containerWidth }: MiniAppProps) {
  const productMiniAppState = useAtomValue(productMiniAppStateAtom)

  const [tasks, setTasks] = React.useState<ChatTask[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)


  // Guard to prevent onNodeCreated from re-triggering during fetch
  // (fetched tasks register as "new" in graphStore, causing an infinite loop)
  const isFetchingRef = React.useRef(false)

  const fetchTasks = useCallback(async () => {
    if (!productMiniAppState.workspace?.id) return
    if (isFetchingRef.current) return
    isFetchingRef.current = true

    setLoading(true)
    setError(null)

    try {
      // Query workspace tasks filtered to user-facing chat agents only.
      // Ordering is the query's default (`-started_at`, newest first) —
      // Task has no `created_at`; `started_at` is its creation timestamp.
      const results = await Workspace.setQuery(QueryNames.tasksWorkspaceTasks).tasks
        .filter({ __type: ["ChatTask", "ChatSkillsTask", "RFLPTask"] })
        .load({ thread: {}, currently_attached_to: {} })
        .all()
      setTasks(results as unknown as ChatTask[])
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
      setTasks([])
    } finally {
      setLoading(false)
      isFetchingRef.current = false
    }
  }, [productMiniAppState.workspace?.id])

  useEffect(() => {
    if (!productMiniAppState.workspace?.id) return

    // Initial fetch
    fetchTasks()

    // Subscribe to GraphStore node creation events
    // Refresh when any Task-like node is created (supports multiple agent types)
    const unsubscribe = graphStore.onNodeCreated((node, nodeType) => {
      if (nodeType?.endsWith('Task')) {
        fetchTasks()
      }
    })

    return () => unsubscribe()
  }, [productMiniAppState.workspace?.id, fetchTasks])

  const handleTaskDeleted = (taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId))
  }

  const hasChats = tasks.length > 0;

  return (
    // Definite width (from the measured panel width) is required: the
    // mini-app lives inside a ScrollArea whose viewport lets content grow
    // to its intrinsic width, so without this the no-wrap chat titles would
    // overflow horizontally instead of truncating, pushing the hover icons
    // off the right edge. A fixed width makes the rows truncate and keeps
    // the icons tracking the panel's right edge as it resizes.
    <div className="pl-[4px] box-border overflow-x-hidden" style={{ width: containerWidth }}>
      {/* Header */}
      <div className="flex items-center justify-between px-2 py-1">
        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight my-1">Chats</h4>
      </div>

      <div className="border-none">
        {loading && tasks.length === 0 ? (
          <div className="px-2 py-2 text-sm text-gray-500 italic">
            Loading chats...
          </div>
        ) : error ? (
          <div className="px-2 py-2 text-sm text-red-600">
            Error: {error}
          </div>
        ) : hasChats ? (
          <ul className="p-0 list-none">
            {tasks.map((task: ChatTask) => (
              <TaskItem
                key={task.id as string}
                task={task}
                containerWidth={containerWidth}
                onDelete={handleTaskDeleted}
              />
            ))}
          </ul>
        ) : (
          <div className="px-2 py-2 text-sm text-gray-500 italic">
            No Chats Yet
          </div>
        )}
      </div>
    </div>
  )
}

